import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getTokenPayload, authFetch } from './auth';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config';

const API_BASE = API_BASE_URL;

// --- Fix for default Leaflet icon ---
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// --- Main App Component ---
export default function App({ theme }) {
    const [reports, setReports] = useState([]);
    const [view, setView] = useState('map');
    const [loading, setLoading] = useState(true);
    const [appError, setAppError] = useState(null);
    const isAdmin = getTokenPayload()?.role === 'admin';
    const authPayload = getTokenPayload();
    const isAuthed = !!authPayload;
    const navigate = useNavigate();

    // --- Data Fetching from Backend ---
    const fetchReports = async () => {
        try {
            setLoading(true);
            const response = await authFetch(`${API_BASE}/api/reports`);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setReports(data);
        } catch (error) {
            console.error("Failed to fetch reports:", error);
            setAppError("Could not connect to the backend server. Is it running?");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports();
    }, []);

    useEffect(() => { if (isAdmin && view === 'report') setView('map'); }, [isAdmin, view]);
    useEffect(() => { if (!isAuthed && view === 'report') setView('map'); }, [isAuthed, view]);

    // Filter reports based on user role and status
    // "show only currently pending issues on the map for all users" -> Public sees 'Reported'
    // "show all resolved , undergoing for only uploaded user" -> Owner sees everything
    const visibleReports = reports.filter(report => {
        if (isAdmin) return true;
        const isOwner = authPayload?.id === report.userId;
        // If owner, show everything (Resolved, Undergoing/In Progress, Rejected, Reported)
        if (isOwner) return true;
        // If not owner, show only 'Reported' (Pending)
        return report.status === 'Reported';
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[50vh] text-main">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                    <div className="text-xl font-display font-bold tracking-wider animate-pulse">LOADING ROADWATCH...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 pb-8 max-w-7xl relative">
            {/* Background Effects specifically for Dashboard */}
            <div className="fixed top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary-600/10 via-transparent to-transparent pointer-events-none z-[-1]"></div>

            <div className="mb-6">
                {appError && (
                    <div className="glass-panel border-l-4 border-red-500 text-red-500 p-4 rounded-r-xl mb-6 shadow-lg flex items-start gap-3" role="alert">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 stroke-current shrink-0" fill="none" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        <div>
                            <p className="font-bold">System Error</p>
                            <p className="text-sm opacity-80">{appError}</p>
                        </div>
                    </div>
                )}

                {/* Navigation Menu */}
                <nav className="glass p-1.5 rounded-2xl mb-4 md:mb-8 flex justify-start md:justify-center items-center space-x-1 md:space-x-2 max-w-full md:max-w-fit mx-auto shadow-xl shadow-black/20 overflow-x-auto no-scrollbar touch-pan-x">
                    {!isAdmin && (
                        <>
                            <NavButton active={view === 'map'} onClick={() => setView('map')} icon="map">Map View</NavButton>
                            <NavButton active={view === 'list'} onClick={() => setView('list')} icon="list">Reports</NavButton>
                            {isAuthed && (
                                <NavButton active={view === 'report'} onClick={() => setView('report')} icon="plus">Report Damage</NavButton>
                            )}
                        </>
                    )}
                    {isAdmin && (
                        <>
                            <NavButton active={view === 'map'} onClick={() => setView('map')} icon="map">Live Map</NavButton>
                            <NavButton active={false} onClick={() => navigate('/admin')} icon="dashboard" special>Admin Dashboard</NavButton>
                            <NavButton active={view === 'list'} onClick={() => setView('list')} icon="list">Manage Reports</NavButton>
                        </>
                    )}
                </nav>

                <div className="flex-grow transition-all duration-500 ease-in-out">
                    {view === 'map' && <MapView reports={visibleReports} theme={theme} />}
                    {view === 'list' && <ReportsList
                        reports={visibleReports}
                        isAdmin={isAdmin}
                        authPayload={authPayload}
                        onDeleted={(id) => setReports(r => r.filter(rep => rep._id !== id))}
                    />}
                    {!isAdmin && view === 'report' && <ReportForm setView={setView} onReportSubmitted={fetchReports} />}
                </div>
            </div>
        </div>
    );
}

// Helper Components
function NavButton({ children, active, onClick, icon, special }) {
    const baseClasses = "px-3 md:px-4 py-2 md:py-2.5 text-xs md:text-base rounded-xl font-medium transition-all duration-300 flex items-center gap-1.5 md:gap-2 whitespace-nowrap shrink-0";
    const activeClasses = "bg-primary-600 text-white shadow-lg shadow-primary-600/30 scale-100 md:scale-105";
    // Changed to use theme variables: text-muted -> text-main
    const inactiveClasses = "text-muted hover:text-main hover:bg-white/10 dark:hover:bg-white/5";
    const specialClasses = "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-600/30 hover:scale-105";

    return (
        <button onClick={onClick} className={`${baseClasses} ${special ? specialClasses : (active ? activeClasses : inactiveClasses)}`}>
            {icon === 'map' && <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0121 18.382V7.618a1 1 0 01-.553-.894L15 4m0 13V4m0 0L9 7" /></svg>}
            {icon === 'list' && <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>}
            {icon === 'plus' && <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>}
            {icon === 'dashboard' && <svg className="w-3.5 h-3.5 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>}
            <span>{children}</span>
        </button>
    )
}

// --- Map View Component ---
function MapView({ reports, theme }) {
    const defaultCenter = [20.5937, 78.9629];
    const mapCenter = reports.length > 0 && reports[0].location ? [reports[0].location.latitude, reports[0].location.longitude] : defaultCenter;
    const isDark = theme !== 'bw'; // Default to dark if undefined or 'dark'

    return (
        <div className="h-[calc(100vh-160px)] md:h-[75vh] w-full rounded-2xl glass-panel border border-white/10 overflow-hidden shadow-2xl shadow-black/60 relative group">
            {/* Decorative Elements */}
            <div className="absolute inset-0 pointer-events-none border-[6px] border-white/5 rounded-2xl z-[400]"></div>

            <MapContainer center={mapCenter} zoom={reports.length > 0 ? 13 : 5} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }} className="z-10">
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {reports.map(report => (
                    report.location &&
                    <React.Fragment key={report._id}>
                        <Circle center={[report.location.latitude, report.location.longitude]} pathOptions={{ color: '#f43f5e', fillColor: '#f43f5e', fillOpacity: 0.3, weight: 1 }} radius={200} />
                        <Marker position={[report.location.latitude, report.location.longitude]}>
                            <Popup className="glass-popup">
                                <div className="w-60 p-1">
                                    {report.imageUrl && <img src={report.imageUrl} alt="Damaged Road" className="w-full h-32 object-cover rounded-lg mb-3 shadow-md" />}
                                    <h3 className="font-display font-bold text-lg text-gray-800 leading-tight mb-1">{report.description}</h3>
                                    <p className="text-sm font-medium text-gray-500 mb-2">Status: <span className={`uppercase text-xs tracking-wider px-2 py-0.5 rounded border ${report.status === 'Reported' ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200'}`}>{report.status}</span></p>
                                    <p className="text-xs text-gray-400">Reported on: {new Date(report.timestamp).toLocaleDateString()}</p>
                                </div>
                            </Popup>
                        </Marker>
                    </React.Fragment>
                ))}
            </MapContainer>
        </div>
    );
}

// Helper function for deleting reports
async function deleteReport(reportId, token, onDeleted) {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) return;
    try {
        const url = `${API_BASE}/api/reports/${reportId}`;
        const res = await authFetch(url, { method: 'DELETE', token });
        if (res.ok) {
            onDeleted(reportId);
        } else {
            const errorData = await res.json();
            alert(`Failed to delete report: ${errorData.message || res.statusText}`);
        }
    } catch (err) {
        alert('Error: ' + err.message);
    }
}

// --- Reports List Component ---
function ReportsList({ reports, isAdmin, authPayload, onDeleted }) {
    if (reports.length === 0) {
        return (
            <div className="glass-panel p-12 rounded-2xl flex flex-col items-center justify-center text-center animate-fade-in-up">
                <div className="bg-dark-800/50 p-6 rounded-full mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                </div>
                <h3 className="text-2xl font-display font-bold text-main mb-2">No Reports Yet</h3>
                <p className="text-muted max-w-md mx-auto">
                    Road damage reports submitted by the community will appear here. Be the first to report an issue!
                </p>
            </div>
        );
    }

    const reportCounts = reports.reduce((acc, curr) => {
        acc[curr.status] = (acc[curr.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="glass-panel p-6 md:p-8 rounded-2xl animate-fade-in-up">
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h2 className="text-3xl font-display font-bold text-main">Recent Reports</h2>
                    <p className="text-muted mt-2">Real-time submissions from the community.</p>
                </div>
                <div className="hidden md:flex gap-3">
                    {['Reported', 'In Progress', 'Resolved'].map(status => (
                        reportCounts[status] ? (
                            <div key={status} className="px-3 py-1 bg-dark-800/30 rounded-lg border border-white/5 text-xs text-muted">
                                <span className="font-bold text-main mr-1">{reportCounts[status]}</span> {status}
                            </div>
                        ) : null
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {reports.map(report => {
                    const canDelete = isAdmin || (report.userId && report.userId === authPayload?.id);
                    return (
                        <div key={report._id} className="group p-5 bg-dark-800/10 hover:bg-dark-800/20 dark:bg-dark-800/50 dark:hover:bg-dark-800 border border-white/5 hover:border-primary-500/20 rounded-xl transition-all duration-300 flex flex-col md:flex-row gap-5 relative overflow-hidden">
                            {/* Status Indicator Bar */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${report.status === 'Resolved' ? 'bg-green-500' :
                                report.status === 'In Progress' ? 'bg-blue-500' :
                                    report.status === 'Rejected' ? 'bg-red-500' : 'bg-amber-500'
                                }`}></div>

                            <div className="shrink-0 relative overflow-hidden rounded-lg w-full md:w-48 h-48 md:h-auto">
                                {report.imageUrl ? (
                                    <img
                                        src={report.imageUrl}
                                        alt="Report evidence"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    />
                                ) : (
                                    <div className="w-full h-full bg-dark-800 flex items-center justify-center text-muted">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60"></div>
                                <div className="absolute bottom-2 left-2 right-2 flex justify-between items-end">
                                    <span className="text-xs text-white/90 font-mono bg-black/40 px-2 py-0.5 rounded backdrop-blur-sm">
                                        {new Date(report.timestamp).toLocaleDateString()}
                                    </span>
                                </div>
                            </div>

                            <div className="flex-grow flex flex-col justify-between z-10 py-1">
                                <div>
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg text-main group-hover:text-primary-500 transition-colors">{report.description}</h3>
                                        <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-full border ${report.status === 'Resolved' ? 'bg-green-500/10 text-green-500 border-green-500/20' :
                                            report.status === 'In Progress' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                                                report.status === 'Rejected' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                                                    'bg-amber-500/10 text-amber-500 border-amber-500/20'
                                            }`}>
                                            {report.status}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted mt-3">
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                            <span className="truncate">{report.location ? `${report.location.latitude.toFixed(4)}, ${report.location.longitude.toFixed(4)}` : 'Location N/A'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg className="w-4 h-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                            <span className="truncate">{report.userName || 'Anonymous User'}</span>
                                        </div>
                                    </div>
                                </div>

                                {canDelete && (
                                    <div className="mt-4 flex justify-end">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); deleteReport(report._id, authPayload?.token, onDeleted); }}
                                            className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500 hover:text-white transition-all duration-200"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            Delete Report
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div >
    );
}

// --- ReportForm Component ---
function ReportForm({ setView, onReportSubmitted }) {
    const [description, setDescription] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [location, setLocation] = useState(null);
    const [userName, setUserName] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [cameraStream, setCameraStream] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const handleImageChange = (e) => {
        if (e.target.files[0]) {
            setImageFile(e.target.files[0]);
            setCapturedImage(null);
        }
    };

    const openCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            setCameraStream(stream);
            setShowCamera(true);
            if (videoRef.current) videoRef.current.srcObject = stream;
        } catch {
            alert('Cannot access camera.');
        }
    };
    const capturePhoto = () => {
        if (!videoRef.current) return;
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setCapturedImage(dataUrl);
        stopCamera();
    };
    const stopCamera = () => {
        cameraStream?.getTracks()?.forEach(t => t.stop());
        setCameraStream(null);
        setShowCamera(false);
    };
    const retake = () => {
        setCapturedImage(null);
        openCamera();
    };
    useEffect(() => () => stopCamera(), []);

    const getLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
                    setError('');
                },
                () => {
                    setError('Unable to retrieve location. Using default.');
                    setLocation({ latitude: 28.6139, longitude: 77.2090 });
                }
            );
        } else {
            setError('Geolocation not supported. Using default.');
            setLocation({ latitude: 28.6139, longitude: 77.2090 });
        }
    };

    useEffect(() => { getLocation(); }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!description || !location) {
            setError('Please provide a description and location.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        let imageBase64 = null;
        if (capturedImage) {
            imageBase64 = capturedImage;
        } else if (imageFile) {
            const reader = new FileReader();
            reader.readAsDataURL(imageFile);
            await new Promise((resolve, reject) => {
                reader.onload = () => resolve();
                reader.onerror = reject;
            });
            imageBase64 = reader.result;
        }

        try {
            const reportData = {
                description,
                imageUrl: imageBase64,
                location,
                userName: userName || 'Anonymous',
            };

            const response = await authFetch(`${API_BASE}/api/reports`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(reportData),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.message || 'Failed to submit report.');
            }

            onReportSubmitted();
            setView('list');

        } catch (err) {
            setError(err.message);
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="glass-panel p-8 rounded-2xl shadow-2xl max-w-2xl mx-auto border border-white/10 relative overflow-hidden">
            {/* Gradient glow */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl pointer-events-none"></div>

            <h2 className="text-3xl font-display font-bold mb-6 text-center text-main relative z-10">Report Road Damage</h2>

            {error && <div className="bg-red-500/10 border border-red-500/20 text-red-500 font-medium p-3 rounded-lg mb-4 text-sm">{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                <div className="group">
                    <label htmlFor="userName" className="block text-sm font-medium text-muted mb-1 group-focus-within:text-primary-500 transition-colors">Your Name (Optional)</label>
                    <input id="userName" value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="e.g., John Doe" className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 text-main placeholder-muted transition-all" />
                </div>

                <div className="group">
                    <label htmlFor="description" className="block text-sm font-medium text-muted mb-1 group-focus-within:text-primary-500 transition-colors">Description</label>
                    <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the issue... (e.g. Large pothole near the intersection)" className="w-full px-4 py-3 bg-dark-700 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 text-main placeholder-muted transition-all resize-none" rows="4" required></textarea>
                </div>

                <div className="space-y-3">
                    <label className="block text-sm font-medium text-muted mb-1">Upload Evidence</label>

                    <div className="p-4 border-2 border-dashed border-white/10 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-center">
                        <input type="file" id="file-upload" onChange={handleImageChange} accept="image/*" className="hidden" />
                        {!capturedImage && !imageFile && !showCamera && (
                            <div className="flex flex-col items-center gap-3">
                                <div className="flex gap-2">
                                    <label htmlFor="file-upload" className="cursor-pointer px-4 py-2 bg-dark-800 text-white dark:text-gray-200 text-sm font-medium rounded-lg hover:bg-black transition-colors border border-white/10">Upload File</label>
                                    <span className="text-muted self-center">or</span>
                                    <button type="button" onClick={openCamera} className="px-4 py-2 bg-purple-600/20 text-purple-600 dark:text-purple-300 text-sm font-medium rounded-lg hover:bg-purple-600/30 transition-colors border border-purple-500/30">Use Camera</button>
                                </div>
                                <p className="text-xs text-muted">Supported: JPG, PNG, WEBP</p>
                            </div>
                        )}

                        {imageFile && !capturedImage && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-main truncate">{imageFile.name}</span>
                                <button type="button" onClick={() => setImageFile(null)} className="text-red-400 hover:text-red-300">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        )}

                        {showCamera && (
                            <div className="space-y-2">
                                <video ref={videoRef} autoPlay playsInline className="w-full rounded-lg border border-white/20" />
                                <div className="flex gap-2 justify-center">
                                    <button type="button" onClick={capturePhoto} className="px-6 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700">Capture</button>
                                    <button type="button" onClick={stopCamera} className="px-6 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700">Cancel</button>
                                </div>
                            </div>
                        )}

                        {capturedImage && (
                            <div className="space-y-2">
                                <img src={capturedImage} alt="Captured" className="w-full h-48 object-cover rounded-lg border border-white/20" />
                                <div className="flex gap-2 justify-center">
                                    <button type="button" onClick={retake} className="px-4 py-2 bg-amber-600/20 text-amber-500 dark:text-amber-300 border border-amber-500/30 rounded-lg text-sm hover:bg-amber-600/30">Retake</button>
                                    <button type="button" onClick={() => { setCapturedImage(null); }} className="px-4 py-2 bg-red-600/20 text-red-500 dark:text-red-300 border border-red-500/30 rounded-lg text-sm hover:bg-red-600/30">Remove</button>
                                </div>
                            </div>
                        )}
                        <canvas ref={canvasRef} className="hidden" />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-muted mb-1">Status</label>
                    <div className="flex items-center justify-between p-4 bg-dark-800/20 dark:bg-dark-800/50 border border-white/10 rounded-xl">
                        <div className="text-sm">
                            <span className="block text-muted text-xs">Lat/Long</span>
                            {location ? <span className="font-mono text-emerald-500 dark:text-emerald-400 font-bold">{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}</span> : <span className="text-muted">Waiting for location...</span>}
                        </div>
                        <button type="button" onClick={getLocation} className="p-2 bg-white/5 rounded-lg hover:bg-white/10 border border-white/5 transition-colors text-main" title="Refresh Location">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        </button>
                    </div>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-500 hover:to-indigo-500 text-white font-bold py-4 px-6 rounded-xl shadow-lg shadow-primary-600/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2">
                    {isSubmitting ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            Submitting Report...
                        </>
                    ) : (
                        'Submit Report'
                    )}
                </button>
            </form>
        </div>
    );
}